import React from "react";
import { Dropdown, DropdownProps, makeStyles, mergeClasses, Option, tokens } from "@fluentui/react-components";
import { PositioningShorthand } from "@fluentui/react-positioning";
import { TruncatedText } from "./TruncatedText";
import { useTheme } from "../../hooks/useTheme";

const useStyles = makeStyles({
    dropdown: {
        minWidth: 0,
        width: "100%",
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    dropdownHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
});

export interface NewformaDropdownOption {
    key: string;
    text: string;
}

export interface NewformaDropdownProps
    extends Pick<
        DropdownProps,
        "disabled" | "placeholder" | "value" | "selectedOptions" | "onOptionSelect" | "listbox"
    > {
    options: NewformaDropdownOption[];
    className?: string;
    "data-testid"?: string;
    positioning?: PositioningShorthand;
}

const NewformaDropdown: React.FC<NewformaDropdownProps> = ({
    options,
    className,
    placeholder,
    disabled,
    value,
    positioning,
    listbox,
    ...dropdownProps
}) => {
    const { isDarkMode } = useTheme();
    const styles = useStyles();

    // If value is undefined but there are selectedOptions, derive display text from options
    // to prevent Fluent's Dropdown from applying placeholder styling on a valid selection
    const resolvedValue =
        value ??
        (dropdownProps.selectedOptions?.length
            ? options.find((o) => dropdownProps.selectedOptions!.includes(o.key))?.text
            : undefined);
    const displayText = resolvedValue || placeholder || "";

    return (
        <Dropdown
            className={mergeClasses(styles.dropdown, !disabled && styles.dropdownHover, className)}
            appearance={isDarkMode ? "filled-darker" : "outline"}
            placeholder={placeholder}
            value={resolvedValue}
            disabled={disabled}
            disableAutoFocus
            positioning={positioning}
            listbox={listbox}
            button={{
                children: <TruncatedText text={displayText} />,
            }}
            {...dropdownProps}
        >
            {options.map((option) => (
                <Option key={option.key} value={option.key} text={option.text}>
                    <TruncatedText text={option.text} />
                </Option>
            ))}
        </Dropdown>
    );
};

export default NewformaDropdown;
